import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useAuth } from '@/hooks/useAuth';
import { useHostWorkspace } from '@/hooks/useHostWorkspace';
import { useBrandFavicon } from '@/hooks/useBrandFavicon';
import { resolveHomeRoute } from '@/lib/resolveHomeRoute';
import { FullScreenLoader } from '@/components/common/FullScreenLoader';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { LoginScreen } from '@/components/auth/LoginScreen';
import { SignUpScreen } from '@/components/auth/SignUpScreen';
import { ForgotPasswordScreen } from '@/components/auth/ForgotPasswordScreen';
import { ResetPasswordScreen } from '@/components/auth/ResetPasswordScreen';
import { VerifyEmailChangeScreen } from '@/components/auth/VerifyEmailChangeScreen';
import { VerifyEmailScreen } from '@/components/auth/VerifyEmailScreen';
import { UnsubscribeScreen } from '@/components/public/UnsubscribeScreen';
import { LegalDocumentPage } from '@/components/legal/LegalDocumentPage';
import { AcceptInvitationScreen } from '@/components/auth/AcceptInvitationScreen';
import { AccessErrorScreen } from '@/components/auth/AccessErrorScreen';
import { RequireAuthenticatedSession } from '@/components/auth/RequireAuthenticatedSession';
import { RequirePlatformAccess } from '@/components/auth/RequirePlatformAccess';
import { RedirectToPlatform } from '@/components/auth/RedirectToPlatform';
import { RequirePractitionerAccess } from '@/components/auth/RequirePractitionerAccess';
import { RequireClientAccess } from '@/components/auth/RequireClientAccess';
import { RequireClientConsents } from '@/components/client/ConsentGate/RequireClientConsents';
import { ClientShell } from '@/components/layout/ClientShell';
import { AdminShell } from '@/components/layout/AdminShell';
import { AdminDashboard } from '@/components/admin/AdminDashboard';
import { PractitionersPage } from '@/components/admin/PractitionersPage';
import { TenantDetailPage } from '@/components/admin/TenantDetailPage';
import { AccessControlPage } from '@/components/admin/AccessControlPage';
import { AdminProfile } from '@/components/admin/AdminProfile';
import { AssistantConfigurationPage } from '@/components/admin/AssistantConfigurationPage';
import { PractitionerShell } from '@/components/layout/PractitionerShell';
import { NotificationCenterPage } from '@/components/notifications/NotificationCenterPage';
import { PractitionerDashboard } from '@/components/practitioner/PractitionerDashboard';
import { ClientsPage } from '@/components/practitioner/ClientsPage';
import { ClientImportPage } from '@/components/practitioner/ClientImportPage';
import { ClientDetailPage } from '@/components/practitioner/ClientDetailPage';
import { PatientsPage } from '@/components/practitioner/PatientsPage';
import { PatientDetailPage } from '@/components/practitioner/PatientDetailPage';
import { SessionNoteEditor } from '@/components/practitioner/SessionNoteEditor';
import { FirstConsultationEditor } from '@/components/practitioner/FirstConsultationEditor';
import { TreatmentPlanEditor } from '@/components/practitioner/TreatmentPlanEditor';
import { ConsentsPage } from '@/components/practitioner/ConsentsPage';
import { IntakeInboxPage } from '@/components/practitioner/IntakeInboxPage';
import { IntakeSubmissionPage } from '@/components/practitioner/IntakeSubmissionPage';
import { WorkspaceSettingsPage } from '@/components/practitioner/WorkspaceSettingsPage';
import { ReportsPage } from '@/components/practitioner/ReportsPage';
import { OrganizationPage } from '@/components/practitioner/OrganizationPage';
import { CalendarSyncCallbackPage } from '@/components/practitioner/CalendarSyncCallbackPage';
import { ChatPage as PractitionerChatPage } from '@/components/practitioner/ChatPage';
import { BroadcastsPage } from '@/components/practitioner/BroadcastsPage';
import { ComposeBroadcast } from '@/components/practitioner/BroadcastsPage/ComposeBroadcast';
import { BroadcastDetail } from '@/components/practitioner/BroadcastsPage/BroadcastDetail';
import { SupportPage as PractitionerSupportPage } from '@/components/practitioner/SupportPage';
import { SupportPage as AdminSupportPage } from '@/components/admin/SupportPage';
import { HomePage as ClientPortalHomePage } from '@/components/client/ClientPortal/HomePage';
import { PetsPage as ClientPortalPetsPage } from '@/components/client/ClientPortal/PetsPage';
import { VisitsPage as ClientPortalVisitsPage } from '@/components/client/ClientPortal/VisitsPage';
import { FormsPage as ClientPortalFormsPage } from '@/components/client/ClientPortal/FormsPage';
import { AccountPage as ClientPortalAccountPage } from '@/components/client/ClientPortal/AccountPage';
import { AnnouncementsPage as ClientAnnouncementsPage } from '@/components/client/AnnouncementsPage';

const ClientPortalPetPage = lazy(function loadClientPortalPetPage() {
  return import('@/components/client/ClientPortal/PetPage').then(
    function resolveModule(petPageModule) {
      return { default: petPageModule.PetPage };
    }
  );
});

const IntakeForm = lazy(function loadIntakeForm() {
  return import('@/components/client/ClientPortal/IntakeForm').then(
    function resolveModule(intakeFormModule) {
      return { default: intakeFormModule.IntakeForm };
    }
  );
});

const ClientChatPage = lazy(function loadClientChatPage() {
  return import('@/components/client/ChatPage').then(function resolveModule(chatPageModule) {
    return { default: chatPageModule.ChatPage };
  });
});

function PortalRouteLoader() {
  return (
    <div className="p-4 sm:p-6 md:p-8 py-16 flex justify-center">
      <LoadingSpinner className="w-7 h-7" />
    </div>
  );
}

function HomeRedirect() {
  const { status, platformPermissions, activeScopeCode } = useAuth();
  if (status === 'loading') {
    return <FullScreenLoader />;
  }
  if (status === 'unauthenticated') {
    return <Navigate to={APPLICATION_ROUTES.login} replace />;
  }
  return <Navigate to={resolveHomeRoute(activeScopeCode, platformPermissions)} replace />;
}

export function App() {
  useBrandFavicon();
  const hostWorkspace = useHostWorkspace();
  return (
    <Routes>
      <Route path={APPLICATION_ROUTES.login} element={<LoginScreen />} />
      <Route
        path={APPLICATION_ROUTES.signUp}
        element={hostWorkspace === null ? <SignUpScreen /> : <RedirectToPlatform />}
      />
      <Route path={APPLICATION_ROUTES.forgotPassword} element={<ForgotPasswordScreen />} />
      <Route path={APPLICATION_ROUTES.resetPassword} element={<ResetPasswordScreen />} />
      <Route path={APPLICATION_ROUTES.verifyEmail} element={<VerifyEmailScreen />} />
      <Route path={APPLICATION_ROUTES.verifyEmailChange} element={<VerifyEmailChangeScreen />} />
      <Route path={APPLICATION_ROUTES.acceptInvitation} element={<AcceptInvitationScreen />} />
      <Route path={APPLICATION_ROUTES.unsubscribe} element={<UnsubscribeScreen />} />
      <Route path={APPLICATION_ROUTES.legalDocument} element={<LegalDocumentPage />} />

      <Route element={<RequireAuthenticatedSession />}>
        <Route path={APPLICATION_ROUTES.accessError} element={<AccessErrorScreen />} />
        <Route element={<RequirePlatformAccess />}>
          <Route element={<AdminShell />}>
            <Route path={APPLICATION_ROUTES.adminDashboard} element={<AdminDashboard />} />
            <Route path={APPLICATION_ROUTES.adminPractitioners} element={<PractitionersPage />} />
            <Route path={APPLICATION_ROUTES.adminTenantDetail} element={<TenantDetailPage />} />
            <Route path={APPLICATION_ROUTES.adminAccessControl} element={<AccessControlPage />} />
            <Route
              path={APPLICATION_ROUTES.adminUsers}
              element={<Navigate to={APPLICATION_ROUTES.adminAccessControl} replace />}
            />
            <Route
              path={APPLICATION_ROUTES.adminRoles}
              element={<Navigate to={APPLICATION_ROUTES.adminAccessControl} replace />}
            />
            <Route
              path={APPLICATION_ROUTES.adminAuditLog}
              element={<Navigate to={APPLICATION_ROUTES.adminAccessControl} replace />}
            />
            <Route path={APPLICATION_ROUTES.adminSupport} element={<AdminSupportPage />} />
            <Route path={APPLICATION_ROUTES.adminSupportTicket} element={<AdminSupportPage />} />
            <Route
              path={APPLICATION_ROUTES.adminNotifications}
              element={<NotificationCenterPage />}
            />
            <Route
              path={APPLICATION_ROUTES.adminAssistant}
              element={<AssistantConfigurationPage />}
            />
            <Route path={APPLICATION_ROUTES.adminProfile} element={<AdminProfile />} />
          </Route>
        </Route>
        <Route element={<RequirePractitionerAccess />}>
          <Route
            path={APPLICATION_ROUTES.calendarSyncCallback}
            element={<CalendarSyncCallbackPage />}
          />
          <Route element={<PractitionerShell />}>
            <Route
              path={APPLICATION_ROUTES.workspaceDashboard}
              element={<PractitionerDashboard />}
            />
            <Route
              path={APPLICATION_ROUTES.workspaceBranding}
              element={<Navigate to={APPLICATION_ROUTES.workspaceSettings} replace />}
            />
            <Route path={APPLICATION_ROUTES.workspaceClients} element={<ClientsPage />} />
            <Route path={APPLICATION_ROUTES.workspaceClientImport} element={<ClientImportPage />} />
            <Route path={APPLICATION_ROUTES.workspaceClientDetail} element={<ClientDetailPage />} />
            <Route path={APPLICATION_ROUTES.workspacePatients} element={<PatientsPage />} />
            <Route
              path={APPLICATION_ROUTES.workspacePatientSessionNote}
              element={<SessionNoteEditor />}
            />
            <Route
              path={APPLICATION_ROUTES.workspacePatientFirstConsultation}
              element={<FirstConsultationEditor />}
            />
            <Route
              path={APPLICATION_ROUTES.workspacePatientTreatmentPlan}
              element={<TreatmentPlanEditor />}
            />
            <Route
              path={APPLICATION_ROUTES.workspacePatientDetail}
              element={<PatientDetailPage />}
            />
            <Route path={APPLICATION_ROUTES.workspaceIntake} element={<IntakeInboxPage />} />
            <Route
              path={APPLICATION_ROUTES.workspaceIntakeSubmission}
              element={<IntakeSubmissionPage />}
            />
            <Route path={APPLICATION_ROUTES.workspaceConsents} element={<ConsentsPage />} />
            <Route
              path={APPLICATION_ROUTES.workspaceSettings}
              element={<WorkspaceSettingsPage />}
            />
            <Route path={APPLICATION_ROUTES.workspaceReports} element={<ReportsPage />} />
            <Route
              path={APPLICATION_ROUTES.workspaceTeam}
              element={<Navigate to={APPLICATION_ROUTES.workspaceOrganizationMembers} replace />}
            />
            <Route path={APPLICATION_ROUTES.workspaceOrganization} element={<OrganizationPage />} />
            <Route
              path={APPLICATION_ROUTES.workspaceNotifications}
              element={<NotificationCenterPage />}
            />
            <Route
              path={APPLICATION_ROUTES.workspaceSupport}
              element={<PractitionerSupportPage />}
            />
            <Route
              path={APPLICATION_ROUTES.workspaceSupportTicket}
              element={<PractitionerSupportPage />}
            />
            <Route path={APPLICATION_ROUTES.workspaceChat} element={<PractitionerChatPage />} />
            <Route
              path={APPLICATION_ROUTES.workspaceChatConversation}
              element={<PractitionerChatPage />}
            />
            <Route path={APPLICATION_ROUTES.workspaceBroadcasts} element={<BroadcastsPage />} />
            <Route
              path={APPLICATION_ROUTES.workspaceBroadcastCompose}
              element={<ComposeBroadcast />}
            />
            <Route
              path={APPLICATION_ROUTES.workspaceBroadcastDetail}
              element={<BroadcastDetail />}
            />
          </Route>
        </Route>
        <Route element={<RequireClientAccess />}>
          <Route element={<RequireClientConsents />}>
            <Route element={<ClientShell />}>
              <Route path={APPLICATION_ROUTES.clientPortal} element={<ClientPortalHomePage />} />
              <Route
                path={APPLICATION_ROUTES.clientPortalPets}
                element={<ClientPortalPetsPage />}
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalPet}
                element={
                  <Suspense fallback={<PortalRouteLoader />}>
                    <ClientPortalPetPage />
                  </Suspense>
                }
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalVisits}
                element={<ClientPortalVisitsPage />}
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalForms}
                element={<ClientPortalFormsPage />}
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalAccount}
                element={<ClientPortalAccountPage />}
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalIntake}
                element={
                  <Suspense fallback={<PortalRouteLoader />}>
                    <IntakeForm />
                  </Suspense>
                }
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalIntakeSubmission}
                element={
                  <Suspense fallback={<PortalRouteLoader />}>
                    <IntakeForm />
                  </Suspense>
                }
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalAnnouncements}
                element={<ClientAnnouncementsPage />}
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalNotifications}
                element={<NotificationCenterPage />}
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalChat}
                element={
                  <Suspense fallback={<PortalRouteLoader />}>
                    <ClientChatPage />
                  </Suspense>
                }
              />
              <Route
                path={APPLICATION_ROUTES.clientPortalChatConversation}
                element={
                  <Suspense fallback={<PortalRouteLoader />}>
                    <ClientChatPage />
                  </Suspense>
                }
              />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="/" element={<HomeRedirect />} />
      <Route path="*" element={<Navigate to={APPLICATION_ROUTES.login} replace />} />
    </Routes>
  );
}
